// frontend/src/components/Toast.js

import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Coins,
  Zap,
  AlertTriangle,
  RefreshCw,
  Clock,
  WifiOff,
  FileWarning,
  CreditCard
} from 'lucide-react';

/**
 * Toast Notification Component - COMPLETE VERSION
 * ================================================
 *
 * Features:
 * ✅ Success, Error, Warning, Info toasts
 * ✅ Credits Warning - Blue/Purple theme
 * ✅ API Error with retry option
 * ✅ Model-specific errors
 * ✅ Rate limit warnings
 * ✅ File upload errors
 */

// ==========================================
// STANDARD TOAST COMPONENT
// ==========================================

const Toast = ({ message, type, credits, onClose, duration = 3000 }) => {
  useEffect(() => {
    // Don't auto-close credits warning with 0 credits
    if (type === 'credits' && credits === 0) return;

    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose, type, credits]);

  // Credits warning - Blue/Purple theme (matching app design)
  if (type === 'credits') {
    return (
      <div className="fixed bottom-4 right-4 z-[9999] animate-slide-in-right bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl shadow-2xl p-4 min-w-[300px] max-w-md">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg">
            <Coins className="text-blue-600" size={20} />
          </div>
          <div className="flex-1">
            <p className="font-bold text-gray-900">
              {credits <= 0 ? '😢 No Credits Left!' : `⚡ Low Credits: ${credits}`}
            </p>
            <p className="text-sm text-gray-600 mt-0.5">
              {credits <= 0
                ? 'Upgrade to continue using MidoLABS'
                : 'Consider upgrading your plan'}
            </p>
            <a
              href="/pricing"
              className="mt-3 flex items-center justify-center space-x-2 w-full py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-600 transition-colors shadow-md"
            >
              <Zap size={16} />
              <span>Get More Credits</span>
            </a>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Standard toasts
  const config = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-600'
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-600'
    },
    warning: {
      icon: AlertCircle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-600'
    },
    info: {
      icon: AlertCircle,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-600'
    }
  };

  const { icon: Icon, bgColor, borderColor, textColor, iconColor } = config[type] || config.info;

  return (
    <div className={`fixed top-4 right-4 z-[9999] animate-slide-in-right ${bgColor} ${borderColor} border-2 rounded-xl shadow-2xl p-4 min-w-[300px] max-w-md`}>
      <div className="flex items-start space-x-3">
        <Icon className={`${iconColor} flex-shrink-0`} size={24} />
        <div className="flex-1">
          <p className={`${textColor} font-semibold text-sm leading-relaxed`}>
            {message}
          </p>
        </div>
        <button
          onClick={onClose}
          className={`${textColor} hover:opacity-70 transition-opacity flex-shrink-0`}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};


// ==========================================
// API ERROR TOAST COMPONENT
// ==========================================

const APIErrorToast = ({ error, onClose, onRetry, duration = 8000 }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  // Determine error type and friendly message
  const getErrorInfo = (error) => {
    const errorStr = typeof error === 'string' ? error : error?.message || 'Unknown error';
    const errorLower = errorStr.toLowerCase();

    if (errorLower.includes('rate limit') || errorLower.includes('429') || errorLower.includes('too many')) {
      return {
        icon: AlertTriangle,
        title: 'Rate Limit Reached',
        message: 'Too many requests. Please wait a moment and try again.',
        color: 'yellow',
        canRetry: true,
        retryDelay: 30000
      };
    }

    if (errorLower.includes('quota') || errorLower.includes('billing') || errorLower.includes('exceeded')) {
      return {
        icon: CreditCard,
        title: 'API Quota Exceeded',
        message: 'The AI provider has reached its quota. Try a different model.',
        color: 'orange',
        canRetry: false
      };
    }

    if (errorLower.includes('timeout') || errorLower.includes('timed out')) {
      return {
        icon: Clock,
        title: 'Request Timeout',
        message: 'The request took too long. This might be a complex prompt - try simplifying it.',
        color: 'blue',
        canRetry: true
      };
    }

    if (errorLower.includes('network') || errorLower.includes('fetch') || errorLower.includes('connection') || errorLower.includes('failed to fetch')) {
      return {
        icon: WifiOff,
        title: 'Connection Error',
        message: 'Unable to connect. Please check your internet connection.',
        color: 'gray',
        canRetry: true
      };
    }

    if (errorLower.includes('file') || errorLower.includes('upload') || errorLower.includes('size') || errorLower.includes('too large')) {
      return {
        icon: FileWarning,
        title: 'File Error',
        message: errorStr,
        color: 'red',
        canRetry: false
      };
    }

    if (errorLower.includes('invalid') || errorLower.includes('400') || errorLower.includes('bad request')) {
      return {
        icon: AlertCircle,
        title: 'Invalid Request',
        message: 'Please check your input and try again.',
        color: 'red',
        canRetry: false
      };
    }

    if (errorLower.includes('unauthorized') || errorLower.includes('401') || errorLower.includes('token')) {
      return {
        icon: AlertTriangle,
        title: 'Session Expired',
        message: 'Please log in again to continue.',
        color: 'yellow',
        canRetry: false
      };
    }

    if (errorLower.includes('forbidden') || errorLower.includes('403')) {
      return {
        icon: AlertTriangle,
        title: 'Access Denied',
        message: 'You don\'t have permission to perform this action.',
        color: 'red',
        canRetry: false
      };
    }

    if (errorLower.includes('not found') || errorLower.includes('404')) {
      return {
        icon: AlertCircle,
        title: 'Not Found',
        message: 'The requested resource was not found.',
        color: 'gray',
        canRetry: false
      };
    }

    if (errorLower.includes('server') || errorLower.includes('500') || errorLower.includes('internal')) {
      return {
        icon: XCircle,
        title: 'Server Error',
        message: 'Something went wrong on our end. Please try again later.',
        color: 'red',
        canRetry: true
      };
    }

    // Default error
    return {
      icon: XCircle,
      title: 'Something Went Wrong',
      message: errorStr.length > 100 ? errorStr.substring(0, 100) + '...' : errorStr,
      color: 'red',
      canRetry: true
    };
  };

  const errorInfo = getErrorInfo(error);
  const Icon = errorInfo.icon;

  const colorClasses = {
    red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', text: 'text-red-800', btn: 'bg-red-600 hover:bg-red-700' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-600', text: 'text-yellow-800', btn: 'bg-yellow-600 hover:bg-yellow-700' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', text: 'text-orange-800', btn: 'bg-orange-600 hover:bg-orange-700' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', text: 'text-blue-800', btn: 'bg-blue-600 hover:bg-blue-700' },
    gray: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600', text: 'text-gray-800', btn: 'bg-gray-600 hover:bg-gray-700' }
  };

  const colors = colorClasses[errorInfo.color] || colorClasses.red;

  return (
    <div className={`fixed bottom-4 right-4 z-[9999] animate-slide-in-right ${colors.bg} ${colors.border} border-2 rounded-xl shadow-2xl p-4 min-w-[320px] max-w-md`}>
      <div className="flex items-start space-x-3">
        <div className={`p-2 ${colors.bg} rounded-lg flex-shrink-0`}>
          <Icon className={colors.icon} size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-bold ${colors.text}`}>{errorInfo.title}</p>
          <p className="text-sm text-gray-600 mt-1 break-words">{errorInfo.message}</p>

          {errorInfo.canRetry && onRetry && (
            <button
              onClick={() => {
                onClose();
                onRetry();
              }}
              className="mt-3 flex items-center space-x-2 px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
            >
              <RefreshCw size={14} />
              <span>Try Again</span>
            </button>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};


// ==========================================
// TOAST CONTAINER & MANAGER
// ==========================================

let setToastQueue = null;

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    setToastQueue = setToasts;
    return () => {
      setToastQueue = null;
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <>
      {toasts.map((toast) => {
        // API Error Toast
        if (toast.type === 'api_error') {
          return (
            <APIErrorToast
              key={toast.id}
              error={toast.error}
              onClose={() => removeToast(toast.id)}
              onRetry={toast.onRetry}
              duration={toast.duration}
            />
          );
        }

        // Standard Toast (including credits)
        return (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            credits={toast.credits}
            onClose={() => removeToast(toast.id)}
            duration={toast.duration}
          />
        );
      })}
    </>
  );
};


// ==========================================
// GLOBAL TOAST FUNCTIONS
// ==========================================

/**
 * Show a standard toast notification
 * @param {string} message - The message to display
 * @param {string} type - 'success' | 'error' | 'warning' | 'info'
 * @param {number} duration - Duration in milliseconds
 */
export const showToast = (message, type = 'info', duration = 3000) => {
  const id = Date.now() + Math.random();

  if (setToastQueue) {
    setToastQueue(prev => [...prev, { id, message, type, duration }]);

    setTimeout(() => {
      if (setToastQueue) {
        setToastQueue(prev => prev.filter(t => t.id !== id));
      }
    }, duration + 500);
  } else {
    console.log(`[${type.toUpperCase()}] ${message}`);
  }
};

/**
 * Show credits warning toast
 * @param {number} credits - Remaining credits
 */
export const showCreditsWarning = (credits) => {
  const id = Date.now() + Math.random();
  const duration = credits <= 0 ? 0 : 8000; // Don't auto-close if 0 credits

  if (setToastQueue) {
    setToastQueue(prev => [...prev, { id, type: 'credits', credits, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        if (setToastQueue) {
          setToastQueue(prev => prev.filter(t => t.id !== id));
        }
      }, duration + 500);
    }
  }
};

/**
 * Check user credits and show warning if needed
 * @param {object} user - User object with credits property
 */
export const checkCreditsAndWarn = (user) => {
  if (!user) return;

  const credits = user.credits || 0;

  if (credits === 0) {
    showCreditsWarning(0);
  } else if (credits <= 5) {
    showCreditsWarning(credits);
  }
};

/**
 * Show API error with optional retry button
 * @param {string|Error} error - The error to display
 * @param {Function} onRetry - Optional callback for retry button
 */
export const showAPIError = (error, onRetry = null) => {
  const id = Date.now() + Math.random();
  const duration = 8000;

  if (setToastQueue) {
    setToastQueue(prev => [...prev, {
      id,
      type: 'api_error',
      error: typeof error === 'string' ? error : error?.message || 'Unknown error',
      onRetry,
      duration
    }]);

    setTimeout(() => {
      if (setToastQueue) {
        setToastQueue(prev => prev.filter(t => t.id !== id));
      }
    }, duration + 500);
  } else {
    console.error('[API ERROR]', error);
  }
};

/**
 * Show file upload error
 * @param {string} message - Error message
 */
export const showFileError = (message) => {
  showToast(message, 'error', 5000);
};

/**
 * Show rate limit warning with countdown
 * @param {number} waitSeconds - Seconds to wait
 */
export const showRateLimitWarning = (waitSeconds = 60) => {
  const id = Date.now() + Math.random();
  const duration = Math.min(waitSeconds * 1000, 30000); // Max 30 seconds display

  if (setToastQueue) {
    setToastQueue(prev => [...prev, {
      id,
      type: 'api_error',
      error: `Rate limit reached. Please wait ${waitSeconds} seconds before trying again.`,
      duration
    }]);

    setTimeout(() => {
      if (setToastQueue) {
        setToastQueue(prev => prev.filter(t => t.id !== id));
      }
    }, duration + 500);
  }
};

/**
 * Show model-specific error with friendly message
 * @param {string} modelName - Name of the model that failed
 * @param {string} error - Error message
 */
export const showModelError = (modelName, error) => {
  const errorLower = error.toLowerCase();

  let friendlyError;
  if (errorLower.includes('quota') || errorLower.includes('billing')) {
    friendlyError = `${modelName}: API quota exceeded. Try a different model.`;
  } else if (errorLower.includes('timeout')) {
    friendlyError = `${modelName}: Request timed out. Try a simpler prompt.`;
  } else if (errorLower.includes('rate')) {
    friendlyError = `${modelName}: Too many requests. Please wait.`;
  } else if (errorLower.includes('invalid')) {
    friendlyError = `${modelName}: Invalid request. Check your prompt.`;
  } else {
    friendlyError = `${modelName} failed: ${error.substring(0, 50)}${error.length > 50 ? '...' : ''}`;
  }

  showToast(friendlyError, 'error', 5000);
};

/**
 * Show success toast for completed benchmark
 * @param {number} successCount - Number of successful models
 * @param {number} totalCount - Total number of models
 * @param {number} creditsUsed - Credits used
 */
export const showBenchmarkComplete = (successCount, totalCount, creditsUsed = 0) => {
  if (successCount === totalCount) {
    showToast(`✅ All ${totalCount} models completed! ${creditsUsed > 0 ? `(${creditsUsed} credits)` : ''}`, 'success', 4000);
  } else if (successCount > 0) {
    showToast(`⚠️ ${successCount}/${totalCount} models completed. Some failed.`, 'warning', 5000);
  } else {
    showToast('❌ All models failed. Please try again.', 'error', 5000);
  }
};

/**
 * Show refund notification
 * @param {number} refundedCredits - Credits refunded
 * @param {Array} failedModels - Array of failed model names
 */
export const showRefundNotification = (refundedCredits, failedModels = []) => {
  const modelList = failedModels.length > 0 ? ` (${failedModels.join(', ')})` : '';
  showToast(`💰 ${refundedCredits} credits refunded for failed models${modelList}`, 'info', 5000);
};


// ==========================================
// EXPORT DEFAULT
// ==========================================

export default Toast;